/**
 * RevisionTracker — adapter-side snapshot-cache invalidation gate.
 *
 * Implements `05-` §G.3 and the D-20 full event-bumping matrix.
 *
 * Phase A scope: the class and its API. Event wiring (attaching to
 * `gantt.attachEvent("onAfterTaskUpdate", ...)`, classifying changed
 * properties as scheduling-relevant vs cosmetic, batchUpdate defer-flush)
 * is Phase B work in `adapters/gantt_adapter.ts`. The class is ready for
 * Phase B to instantiate.
 *
 * Per D-31, this lives under `adapters/` (not `core/`) because it has
 * gantt-aware classification — but adapter code in Phase A is limited to
 * what does NOT touch a live gantt instance. Phase B wires events.
 */

/**
 * Set of task properties whose change DOES bump the revision (per D-20).
 * Anything not in this set is considered cosmetic.
 *
 * The classifier in `classifyTaskUpdate` consults this allowlist when
 * deciding whether a given `onAfterTaskUpdate` event payload should bump.
 *
 * Phase B refines this list as edge cases surface in the test triage.
 */
export const SCHEDULING_RELEVANT_TASK_PROPERTIES: ReadonlySet<string> = new Set([
  "start_date",
  "end_date",
  "duration",
  "constraint_type",
  "constraint_date",
  "calendar_id",
  "auto_scheduling",
  "unscheduled",
  "progress",       // bumps only when useProgress is on (Phase B classifier checks)
  "parent",         // hierarchy reorder
  "type"            // leaf/summary/milestone/placeholder shift
]);

/**
 * Set of `gantt.config.*` keys whose change bumps the revision (per D-20).
 */
export const SCHEDULING_RELEVANT_CONFIG_KEYS: ReadonlySet<string> = new Set([
  "auto_scheduling",
  "auto_scheduling_compatibility",
  "auto_scheduling_strict",
  "auto_scheduling_move_asap_tasks",
  "auto_scheduling_descendant_links",
  "auto_scheduling_initial",
  "auto_scheduling_move_projects",
  "auto_scheduling_use_progress",
  "auto_scheduling_project_constraint",
  "schedule_from_end",
  "auto_scheduling_mode",
  "inherit_calendar",
  "work_time",
  "correct_work_time",
  "duration_unit",
  "duration_step",
  "project_start",
  "project_end",
  "types",
  "links"
]);

/**
 * Revision counter. Phase A: a monotonic counter, Phase B: wired to gantt
 * events. The class is intentionally minimal in Phase A — its API is what
 * the snapshot cache (Phase B) and `core/api.ts` memoization (Phase A) lean
 * against.
 */
export class RevisionTracker {
  private rev: number;
  private batchDepth: number;
  private bumpedDuringBatch: boolean;

  constructor() {
    this.rev = 0;
    this.batchDepth = 0;
    this.bumpedDuringBatch = false;
  }

  /** Current revision. Increments monotonically. */
  get current(): number {
    return this.rev;
  }

  /**
   * Bump the revision. If a batch is in progress, defer to the batch's end
   * (per D-20: batchUpdate flushes once at the end regardless of internal
   * mutations).
   */
  bump(): void {
    if (this.batchDepth > 0) {
      this.bumpedDuringBatch = true;
      return;
    }
    this.rev++;
  }

  /**
   * Enter a batch update. Bumps are deferred until `endBatch`.
   * Refcounted: nested batches increment the depth.
   */
  beginBatch(): void {
    this.batchDepth++;
  }

  /**
   * Exit a batch update. If any internal mutation tried to bump during the
   * batch, the deferred bump fires here (once, regardless of how many
   * internal bumps occurred).
   */
  endBatch(): void {
    if (this.batchDepth === 0) {
      // Defensive — well-formed callers always pair begin/end.
      return;
    }
    this.batchDepth--;
    if (this.batchDepth === 0 && this.bumpedDuringBatch) {
      this.bumpedDuringBatch = false;
      this.rev++;
    }
  }

  /**
   * Classify an `onAfterTaskUpdate` payload as scheduling-relevant or
   * cosmetic.
   *
   * @param changedKeys  the property names that were modified. Adapter
   *   (Phase B) derives this by diffing the old/new task records.
   * @param useProgress  current scheduling config flag — per D-12, progress
   *   changes only matter when `useProgress: true`.
   * @returns `true` if the change should bump the revision.
   */
  classifyTaskUpdate(changedKeys: Iterable<string>, useProgress: boolean): boolean {
    for (const k of changedKeys) {
      if (k === "progress") {
        if (useProgress) return true;
        continue;
      }
      if (SCHEDULING_RELEVANT_TASK_PROPERTIES.has(k)) return true;
    }
    return false;
  }

  /**
   * Classify a `gantt.config.*` mutation as scheduling-relevant.
   *
   * For `auto_scheduling.*` nested keys, the classifier matches the top-
   * level `auto_scheduling` entry — any nested change bumps. Phase B's
   * adapter wires individual config setters.
   */
  classifyConfigChange(key: string): boolean {
    if (SCHEDULING_RELEVANT_CONFIG_KEYS.has(key)) return true;
    // Nested auto_scheduling.* path
    if (key.startsWith("auto_scheduling.")) return true;
    return false;
  }
}
